import React, { useMemo } from 'react'
import './styles/notification-ecomm-options.scss'
import { Well } from '@pushly/aqe/lib/components'
import { Select, Skeleton, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import {
    CatalogItemPickStrategy,
    CatalogItemType,
    CombinedItemStrategy,
    getRankingStrategyOptions,
    getSubscriberScopedOptions,
    isCatalogItemPickStrategy,
    isItemRankingStrategy,
} from '@pushly/models/lib/enums/catalogs'
import { TriggerType } from '@pushly/models/lib/enums/journeys'

interface INotificationCatalogItemOptionsProps {
    className?: string
    options: CombinedItemStrategy[]
    selectedStrategy?: CombinedItemStrategy
    onChange: (strategy: CombinedItemStrategy) => void
    loading?: boolean
    disabled?: boolean
    itemType?: CatalogItemType
    triggerType?: TriggerType
}

const NotificationCatalogItemOptions = (props: INotificationCatalogItemOptionsProps) => {
    const { loading, disabled, options, selectedStrategy, onChange, itemType, triggerType } = props

    if (!options || options.length === 0) {
        return null
    }

    // Determine what strategies are available
    const hasContextStrategies = options.some(opt => isCatalogItemPickStrategy(opt))
    const hasRankingStrategies = options.some(opt => isItemRankingStrategy(opt))

    // Build options based on strategy type
    const scopedOptions = useMemo(() => {
        // Use catalog item pick strategies (personalized)
        if (hasContextStrategies) {
            const contextStrategies = options.filter(opt =>
                isCatalogItemPickStrategy(opt),
            ) as CatalogItemPickStrategy[]

            return getSubscriberScopedOptions(contextStrategies, triggerType)
        }

        // Otherwise use ranking strategies (popular)
        if (hasRankingStrategies) {
            return getRankingStrategyOptions(itemType)
        }

        return []
    }, [options, itemType, triggerType, hasContextStrategies, hasRankingStrategies])

    // Group options by their group field for rendering
    const optionsByGroup = useMemo(() => {
        const grouped: Record<string, typeof scopedOptions> = {}
        scopedOptions.forEach(opt => {
            if (!grouped[opt.group]) {
                grouped[opt.group] = []
            }
            grouped[opt.group].push(opt)
        })
        return grouped
    }, [scopedOptions])

    // Check if selected strategy is valid
    const isValidSelection = scopedOptions.some(opt => opt.value === selectedStrategy)
    const safeValue = isValidSelection ? selectedStrategy : undefined

    const handleStrategyChange = (strategy: CombinedItemStrategy) => {
        onChange(strategy)
    }

    return (
        <Well
            className={getClassNames('notification-ecomm-options-wrapper', {})}
            title={
                <>
                    <span>Item Selection</span>
                    <Tooltip
                        title={
                            <span>
                                Determines how the item that will be included within the notification will be chosen.
                            </span>
                        }
                    >
                        <QuestionCircleOutlined className="info-icon" />
                    </Tooltip>
                </>
            }
            mode="ghost"
            hideFooter={true}
        >
            <Skeleton loading={loading} active={true} avatar={false} title={false}>
                <div className="item-selection-controls">
                    <div className="item-selection-metric">
                        <Select
                            className="ecomm-item-pick-strategy-select"
                            popupClassName="ecomm-item-pick-strategy-dropdown"
                            placeholder="Select how to choose the item"
                            value={safeValue}
                            onChange={handleStrategyChange}
                            disabled={disabled}
                            style={{ width: '100%' }}
                        >
                            {Object.entries(optionsByGroup).map(([group, groupOpts]) => (
                                <Select.OptGroup key={group} label={group}>
                                    {groupOpts.map((option) => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                            ))}
                        </Select>
                    </div>
                </div>
            </Skeleton>
        </Well>
    )
}

export default NotificationCatalogItemOptions
